import { useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, ArrowUpRight, ShieldCheck, LockKeyhole, Star, BadgeCheck, Apple, Play } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import HowItWorks from "@/components/landing/HowItWorks";
import Footer from "@/components/landing/Footer";
import { SERVICES } from "@/components/landing/Services";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const AREAS = [
  "Westminster", "Camden", "Islington", "Hackney", "Tower Hamlets", "Southwark",
  "Lambeth", "Wandsworth", "Kensington & Chelsea", "Hammersmith & Fulham", "Greenwich", "Lewisham",
];

const LONDON_JOBS = [
  { title: "Furniture Assembly", loc: "NW London", price: "£70", offers: "3 offers" },
  { title: "TV Mounting", loc: "Camden", price: "£55", offers: "4 offers" },
  { title: "Garden Tidy-Up", loc: "Islington", price: "£90", offers: "2 offers" },
];

const LONDON_FAQS = [
  {
    q: "Which areas of London do you cover?",
    a: "We cover all of Greater London — including Westminster, Camden, Islington, Hackney, Southwark, Lambeth, Wandsworth, Kensington & Chelsea, Greenwich and more. If you're in London, there's a tasker near you.",
  },
  {
    q: "How quickly can I find someone in London?",
    a: "Most jobs posted in London receive their first offers within hours. London has our largest community of taskers, so availability for same-day and next-day jobs is excellent.",
  },
  {
    q: "How much do services cost in London?",
    a: "You set your budget when you post, and taskers send you offers to compare. Typical London jobs: furniture assembly from £40, TV mounting from £50, cleaning from £30, moving help from £60.",
  },
  {
    q: "Is it safe to hire someone through Your Helper?",
    a: "Yes. Taskers can verify their identity through our verification system, every profile shows ratings and reviews from real previous customers, and payments are handled securely through our payment provider — never cash in hand.",
  },
];

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function London() {
  useEffect(() => {
    document.title = "Task & Home Services in London | Your Helper";
    const descText =
      "Post a job in London and get offers from trusted local taskers — furniture assembly, TV mounting, cleaning, gardening, moving help and more, across every London borough.";
    const titleText = "Task & Home Services in London | Your Helper";

    const setMeta = (selector, content) => {
      const el = document.querySelector(selector);
      const prev = el?.getAttribute("content");
      el?.setAttribute("content", content);
      return () => prev && el?.setAttribute("content", prev);
    };
    const restores = [
      setMeta('meta[name="description"]', descText),
      setMeta('meta[property="og:title"]', titleText),
      setMeta('meta[property="og:description"]', descText),
      setMeta('meta[name="twitter:title"]', titleText),
      setMeta('meta[name="twitter:description"]', descText),
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "london-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: LONDON_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    window.scrollTo(0, 0);

    return () => {
      document.getElementById("london-jsonld")?.remove();
      restores.forEach((r) => r());
    };
  }, []);

  return (
    <div data-testid="london-page">
      <Navbar />
      <main>
        <section data-testid="london-hero" className="relative overflow-hidden pt-[72px]">
          <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-teal/10 blur-3xl pointer-events-none" />
          <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center py-16 md:py-24">
            <div className="lg:col-span-6">
              <motion.div {...fade(0)}>
                <Eyebrow>Now serving Greater London</Eyebrow>
              </motion.div>
              <h1
                data-testid="london-headline"
                className="mt-5 font-display font-extrabold tracking-[-0.03em] leading-[1.04] text-navy text-[2.6rem] sm:text-6xl lg:text-[4.4rem]"
              >
                <span className="block overflow-hidden pb-1">
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    className="block"
                  >
                    Get things done
                  </motion.span>
                </span>
                <span className="block overflow-hidden pb-1">
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="block"
                  >
                    in <span className="text-teal">London.</span>
                  </motion.span>
                </span>
              </h1>
              <motion.p {...fade(0.6)} className="mt-6 text-base sm:text-lg text-slate-600 max-w-md leading-relaxed">
                From furniture assembly in Camden to garden tidy-ups in Greenwich — post a job and get offers from
                trusted local taskers across the capital.
              </motion.p>
              <motion.div {...fade(0.75)} className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="#london-download"
                  data-testid="london-post-job-btn"
                  className="group inline-flex items-center justify-center gap-2 bg-teal hover:bg-teal-hover text-navy font-semibold px-8 py-4 rounded-full shadow-lg shadow-teal/25 transition-colors duration-200"
                >
                  Post a Job
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </a>
                <a
                  href="#london-services"
                  data-testid="london-see-services-btn"
                  className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-dark text-white font-semibold px-8 py-4 rounded-full transition-colors duration-200"
                >
                  See London Services
                </a>
              </motion.div>
              <motion.div {...fade(0.9)} className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-teal" /> Verified people
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <LockKeyhole className="w-4 h-4 text-teal" /> Secure payments
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-teal" /> Reviews you can trust
                </span>
              </motion.div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative max-w-md mx-auto space-y-4">
                {LONDON_JOBS.map((j, i) => (
                  <motion.div
                    key={j.title}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                    data-testid={`london-job-card-${i + 1}`}
                    className={`bg-white rounded-2xl border border-slate-200/80 shadow-xl shadow-navy/5 p-5 flex items-center justify-between ${
                      i === 1 ? "sm:ml-10" : i === 2 ? "sm:ml-20" : ""
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-display font-bold text-navy">{j.title}</h3>
                        <BadgeCheck className="w-4 h-4 text-teal" />
                      </div>
                      <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                        <MapPin className="w-3.5 h-3.5 text-teal" /> {j.loc} · {j.offers}
                      </p>
                    </div>
                    <span className="font-display font-extrabold text-navy text-xl">{j.price}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section data-testid="london-areas" className="py-14 border-y border-slate-200/70 bg-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-teal">
                Areas we cover
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {AREAS.map((a) =>
                  ["Camden", "Islington", "Hackney"].includes(a) ? (
                    <a
                      key={a}
                      href={`/london/${a.toLowerCase()}`}
                      data-testid={`london-area-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-sm font-semibold text-teal bg-teal/[0.07] border border-teal/30 px-4 py-2 rounded-full hover:bg-teal hover:text-white transition-colors duration-200"
                    >
                      {a}
                    </a>
                  ) : (
                    <span
                      key={a}
                      data-testid={`london-area-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-sm font-medium text-navy bg-paper border border-slate-200 px-4 py-2 rounded-full hover:border-teal hover:text-teal transition-colors duration-200"
                    >
                      {a}
                    </span>
                  )
                )}
                <span className="text-sm font-semibold text-teal px-4 py-2">+ every other borough</span>
              </div>
            </Reveal>
          </div>
        </section>

        <section id="london-services" data-testid="london-services-section" className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <Eyebrow>Services in London</Eyebrow>
              <h2 className="mt-4 font-display font-extrabold tracking-tight text-navy text-3xl sm:text-4xl lg:text-5xl max-w-2xl">
                Popular jobs Londoners post every day.
              </h2>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {SERVICES.map((s, i) => (
                <Reveal key={s.name} delay={(i % 4) * 0.08}>
                  <a
                    href="#london-download"
                    data-testid={`london-service-card-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group block bg-white rounded-2xl overflow-hidden border border-slate-200/60 hover:border-teal/40 hover:shadow-xl hover:shadow-navy/5 transition-[box-shadow,border-color] duration-300"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-slate-200">
                      <img
                        src={s.img}
                        alt={`${s.name} in London`}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      />
                      <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <ArrowUpRight className="w-4 h-4 text-navy" />
                      </span>
                    </div>
                    <div className="p-5 flex items-center justify-between">
                      <h3 className="font-display font-bold text-navy text-base">{s.name}</h3>
                      <span className="text-xs font-mono font-medium text-teal">{s.price}</span>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <HowItWorks />

        <section data-testid="london-faq-section" className="py-20 md:py-28 bg-white">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <Reveal className="text-center">
              <Eyebrow>London FAQ</Eyebrow>
              <h2 className="mt-4 font-display font-extrabold tracking-tight text-navy text-3xl sm:text-4xl lg:text-5xl">
                Getting help in London.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Accordion type="single" collapsible className="mt-12" data-testid="london-faq-accordion">
                {LONDON_FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`london-item-${i}`} className="border-slate-200">
                    <AccordionTrigger
                      data-testid={`london-faq-question-${i + 1}`}
                      className="font-display font-bold text-navy text-base sm:text-lg py-6 hover:no-underline hover:text-teal transition-colors duration-200"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent
                      data-testid={`london-faq-answer-${i + 1}`}
                      className="text-sm sm:text-base text-slate-600 leading-relaxed pb-6"
                    >
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <section id="london-download" data-testid="london-download-section" className="relative bg-navy-dark py-24 md:py-32 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-teal/10 blur-3xl pointer-events-none" />
          <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative">
            <Reveal>
              <h2 className="font-display font-extrabold tracking-[-0.03em] text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                London, get the app.
              </h2>
              <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl mx-auto leading-relaxed">
                Post your first job in minutes and get offers from trusted taskers near you.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  type="button"
                  data-testid="london-app-store-badge"
                  className="inline-flex items-center gap-3 bg-white text-navy px-6 py-3 rounded-2xl hover:bg-slate-100 transition-colors duration-200"
                >
                  <Apple className="w-7 h-7" />
                  <span className="text-left leading-tight">
                    <span className="block text-[10px] text-slate-500">Download on the</span>
                    <span className="block font-display font-bold">App Store</span>
                  </span>
                </button>
                <button
                  type="button"
                  data-testid="london-google-play-badge"
                  className="inline-flex items-center gap-3 bg-white text-navy px-6 py-3 rounded-2xl hover:bg-slate-100 transition-colors duration-200"
                >
                  <Play className="w-6 h-6" />
                  <span className="text-left leading-tight">
                    <span className="block text-[10px] text-slate-500">Get it on</span>
                    <span className="block font-display font-bold">Google Play</span>
                  </span>
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
